import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Button,
  Text,
  Divider,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

// Extend window object to include right sidebar update function
declare global {
  interface Window {
    updateRightSidebar?: (content: React.ReactNode | Array<{
      label: string;
      icon?: React.ComponentType;
      colorScheme: string;
      variant?: string;
      onClick?: () => void;
    }> | null) => void;
  }
}

interface RightSidebarProps {
  isOpen: boolean;
  contextActions?: Array<{
    label: string;
    icon?: React.ComponentType;
    colorScheme: string;
    variant?: string;
    onClick?: () => void;
  }>;
}

export const RightSidebar: React.FC<RightSidebarProps> = ({ isOpen, contextActions: externalContextActions }) => {
  const { t } = useTranslation();
  
  // Семантические токены
  const bgColor = 'bg.surface';
  const borderColor = 'border.default';
  const textSecondary = 'text.secondary';

  // State for dynamic content from external sources
  const [dynamicContent, setDynamicContent] = useState<React.ReactNode | Array<{
    label: string;
    icon?: React.ComponentType;
    colorScheme: string;
    variant?: string;
    onClick?: () => void;
  }> | null>(null);

  // Set up global function for updating content
  useEffect(() => {
    window.updateRightSidebar = (content) => {
      setDynamicContent(content);
    };

    // Clean up when component unmounts
    return () => {
      delete window.updateRightSidebar;
    };
  }, []);

  if (!isOpen) return null;

  // Determine what to render based on content type
  const renderContent = () => {
    // If dynamic content is a React node, render it directly
    if (dynamicContent && !Array.isArray(dynamicContent)) {
      return dynamicContent;
    }
    
    // If dynamic content is an array of actions, render as buttons
    if (Array.isArray(dynamicContent) && dynamicContent.length > 0) {
      return (
        <VStack spacing={4} align="stretch">
          <Text fontSize="sm" fontWeight="bold" color={textSecondary}>
            {t('sidebar.rightMenu')}
          </Text>
          
          <Divider />
          
          {dynamicContent.map((action, index) => (
            <Button
              key={index}
              leftIcon={action.icon ? <action.icon /> : undefined}
              colorScheme={action.colorScheme}
              variant={action.variant || "outline"}
              size="sm"
              justifyContent="flex-start"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </VStack>
      );
    }
    
    // Fallback to external context actions
    if (externalContextActions && externalContextActions.length > 0) {
      return (
        <VStack spacing={4} align="stretch">
          <Text fontSize="sm" fontWeight="bold" color={textSecondary}>
            {t('sidebar.rightMenu')}
          </Text>
          
          <Divider />
          
          {externalContextActions.map((action, index) => (
            <Button
              key={index}
              leftIcon={action.icon ? <action.icon /> : undefined}
              colorScheme={action.colorScheme}
              variant={action.variant || "outline"}
              size="sm"
              justifyContent="flex-start"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </VStack>
      );
    }

    return null;
  };

  const content = renderContent();

  // Don't render sidebar if no content
  if (!content) return null;

  return (
    <Box
      position="fixed"
      right={0}
      top="60px"
      width="250px"
      height="calc(100vh - 60px)"
      bg={bgColor}
      borderLeft="1px"
      borderColor={borderColor}
      zIndex={999}
      p={4}
    >
      {content}
    </Box>
  );
}; 